<template>
  <div class="d-flex justify-end centered-on-mobile w-100">
    <loading-dialog :message="messageLoadingDialog" :show-dialog="showLoadingDialog"/>
    <v-btn
        class="block-on-mobile"
        color="secondary"
        size="x-large"
        flat
        rounded="0"
        prepend-icon="mdi-camera"
        @click="triggerFileInput"
    >{{ txtPlaceholder }}
    </v-btn>
    <input
        ref="fileInput"
        class="d-none"
        type="file"
        @change="onDocumentUpload"
        :accept="isInTikTokWebView ? 'image/*' : 'image/*;capture=camera'"
        v-bind="isInTikTokWebView ? { capture: 'environment' } : {}"
    >
  </div>
</template>

<script lang="ts">
import {defineComponent, ref, computed, onMounted} from 'vue';
import {useDocumentStore} from '~/plugins/store/document';
import {useNuxtApp} from '#app';
import LoadingDialog from "~/components/utils/LoadingDialog.vue";
import {translation, useTranslationStore} from "~/plugins/store/translations";
import type {TranslationList} from "~/types/Utils";
import {recaptcha} from '~/composables/recaptcha';
import {useReCaptcha} from "vue-recaptcha-v3";

export default defineComponent({
  components: {LoadingDialog},
  props: {
    txtPlaceholder: String,
    documentId: Number,
  },
  setup(props, {emit}) {
    const {$toast} = useNuxtApp();
    const store = useDocumentStore();
    const documentInput = ref<File | null>(null);
    const uploading = ref(false);
    const placeholderText = ref(props.txtPlaceholder);
    const isInTikTokWebView = ref(false);
    const fileInput = ref(null); // Referință pentru input
    const showLoadingDialog = ref(false);
    const messageLoadingDialog = ref('');
    const translationStore = useTranslationStore();
    const translationList = ref<TranslationList>({});
    const recaptchaInstance = useReCaptcha();

    const triggerFileInput = () => {
      if (fileInput.value) {
        fileInput.value.click();
      }
    };

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');
    });

    const documentList = computed(() => store.documentList);
    const acceptedFormatsForOcr = computed(() => store.acceptedFormatsForOcr);
    const acceptedFormatsForUpload = computed(() => store.acceptedFormatsForUpload);

    const onDocumentUpload = async (event: Event) => {
      const files = (event.target as HTMLInputElement).files;
      if (!files || files.length === 0) {
        $toast.error(translation(translationList.value.error, 'A apărut o eroare'));
        console.error('Fisierul nu a putut fi incarcat');
        return;
      }
      const file = files[0];
      showLoadingDialog.value = true;
      messageLoadingDialog.value = translation(translationList.value.file_loading, 'Se încarcă fișierul...');
      uploading.value = true;

      let fileType = file.type;
      if ((!fileType || !fileType.length) && file.name) {
        fileType = 'image/' + file.name.split('.').pop().toLowerCase();
      }

      if (documentList.value.identity_card_id === props.documentId || documentList.value.vehicle_registration_id === props.documentId) {
        if (acceptedFormatsForOcr.value.includes(fileType)) {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('document_id', props.documentId.toString());
          try {
            const token = await recaptcha(recaptchaInstance);
            formData.append('token', <string>token);
            const response = await store.ocrDocument(formData);
            emit('ocr-response', {response: response, documentId: props.documentId});
            $toast.info(translation(translationList.value.file_processed, 'Fișierul a fost procesat cu succes!'));
            placeholderText.value = file.name;
          } catch (error) {
            $toast.error(translation(translationList.value.error, 'A apărut o eroare'));
            console.error('Eroare la procesarea fisierului');
          }
        } else {
          $toast.warning(translation(translationList.value.file_extension_not_allowed, 'Te rugăm să încarci o imagine în format JPEG, PNG sau WEBP'));
        }
      }

      showLoadingDialog.value = false;
      uploading.value = false;
    };

    return {
      documentInput,
      uploading,
      placeholderText,
      isInTikTokWebView,
      onDocumentUpload,
      triggerFileInput,
      fileInput,
      showLoadingDialog,
      messageLoadingDialog,
      translationList,
      translation
    };
  },
});
</script>

<style scoped>
</style>
